import { useContext } from 'react'
import { transformSidebarsToStructuredObject } from '../../gatsby/utils'
import React, { createContext, useEffect, useState } from 'react'
import { IMenu } from 'components/PostLayout/types'
import { useLocation } from '@reach/router'
import { useStaticQuery, graphql } from 'gatsby'

export const Context = createContext<any>(undefined)

export function recursiveSearch(array, value) {
    for (let i = 0; i < array?.length || 0; i++) {
        const element = array[i]

        if (typeof element === 'string' && element.split('?')[0] === value) {
            return true
        }

        if (typeof element === 'object' && element !== null) {
            const found = recursiveSearch(Object.values(element), value)
            if (found) {
                return true
            }
        }
    }

    return false
}

export interface IProps {
    children: React.ReactNode
    parent?: IMenu
    activeInternalMenu?: IMenu
}

export const LayoutProvider = ({ children, ...other }: IProps) => {
    const data = useStaticQuery(graphql`
        query getLayoutDataQuery {
            layoutData: allMdx(filter: { fields: { slug: { regex: "/^/layout/" } } }) {
                nodes {
                    parent {
                        ... on GoogleDocs {
                            id
                            layout {
                                brand
                                fonts {
                                    fontFamily
                                    src {
                                        url
                                        format
                                    }
                                    fontWeight
                                    fontStyle
                                    fontDisplay
                                }
                                variables {
                                    key
                                    value
                                }
                                sidebars {
                                    name
                                    items {
                                        name
                                        url
                                        enable
                                        badge {
                                            title
                                            className
                                            title_ar
                                        }
                                        children {
                                            name
                                            url
                                            enable
                                            badge {
                                                title
                                                className
                                                title_ar
                                            }
                                            children {
                                                name
                                                url
                                                enable
                                                badge {
                                                    title
                                                    className
                                                    title_ar
                                                }
                                                children {
                                                    name
                                                    url
                                                    enable
                                                    badge {
                                                        title
                                                        className
                                                        title_ar
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                sidebars_ar {
                                    name
                                    items {
                                        name
                                        url
                                        enable
                                        badge {
                                            title
                                            className
                                            title_ar
                                        }
                                        children {
                                            name
                                            url
                                            enable
                                            badge {
                                                title
                                                className
                                                title_ar
                                            }
                                            children {
                                                name
                                                url
                                                enable
                                                badge {
                                                    title
                                                    className
                                                    title_ar
                                                }
                                                children {
                                                    name
                                                    url
                                                    enable
                                                    badge {
                                                        title
                                                        className
                                                        title_ar
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                variables {
                                    key
                                    value
                                }
                                logoFileName
                            }
                            images {
                                name
                                publicURL
                                childImageSharp {
                                    gatsbyImageData
                                }
                            }
                            images2: images {
                                name
                                publicURL
                                childImageSharp {
                                    gatsbyImageData(width: 265, height: 265)
                                }
                            }
                        }
                    }
                }
            }
        }
    `)

    const layout = data.layoutData.nodes[0].parent.layout
    const fonts = layout.fonts
    const variables = layout.variables
    let logoFileName = layout.logoFileName
    const sidebarsRetrived = transformSidebarsToStructuredObject(layout?.sidebars)
    const sidebars_ar = transformSidebarsToStructuredObject(layout?.sidebars_ar)
    let brandRetrived = layout.brand
    const { pathname } = useLocation()
    const [fullWidthContent, setFullWidthContent] = useState<boolean>(
        typeof window !== 'undefined' && localStorage.getItem('full-width-content') === 'true'
    )

    const [logo, setLogo] = useState('Icloudready') // Default logo
    const [sidebars, setSidebars] = useState({}) // Default sidebar
    const [brand, setBrand] = useState('iCloudReady') // Default brand
    const menu = sidebarsRetrived.menu
    let parent
    if (Array.isArray(menu)) {
        parent =
            (other.parent && menu) ??
            menu.find(({ children, url }) => {
                const currentURL = pathname
                return currentURL === url?.split('?')[0] || (children && recursiveSearch(children, currentURL))
            })
    } else {
        // console.warn('menu is not an array or is undefined')
        parent = undefined // or any other fallback value or logic you deem appropriate
    }

    const updateBrandBasedOnEnv = () => {
        if (brandRetrived == '') brandRetrived = null
        const envBrand = brandRetrived || process.env.BRAND
        if (envBrand) {
            setBrand(envBrand)
        }
    }
    const updateLogoBasedOnEnv = () => {
        if (logoFileName == '') logoFileName = null
        const envLogo = logoFileName || process.env.LOGOFILENAME
        if (envLogo) {
            setLogo(envLogo)
        }
    }

    const internalMenu = parent?.children

    const activeInternalMenu =
        other.activeInternalMenu ??
        internalMenu?.find((menuItem) => {
            const currentURL = pathname
            return currentURL === menuItem.url?.split('?')[0] || recursiveSearch(menuItem.children, currentURL)
        })

    useEffect(() => {
        localStorage.setItem('full-width-content', fullWidthContent + '')
    }, [fullWidthContent])

    useEffect(() => {
        // Update logo based on environmental variables or any other logic
        updateLogoBasedOnEnv()
    }, []) // Add dependencies if needed

    useEffect(() => {
        updateBrandBasedOnEnv()
    }, [])

    // Add this useEffect to dynamically load the variables
    useEffect(() => {
        // Loop through each property in the JSON and set it as a CSS variable on the :root element
        for (const { key, value } of variables) {
            document.documentElement.style.setProperty(`--${key}`, value)
        }
    }, []) // Empty dependency array means this will run once when th

    // This useEffect is for loading fonts dynamically
    useEffect(() => {
        let styleContent = ''

        for (const font of fonts) {
            styleContent += `
        @font-face {
          font-family: ${font.fontFamily};
          src: url(${font.src[0].url}) format(${font.src[0].format});
          font-weight: ${font.fontWeight};
          font-style: ${font.fontStyle};
          font-display: swap;
        }
        `
        }

        const styleEl = document.createElement('style')
        styleEl.innerText = styleContent
        document.head.appendChild(styleEl)

        return () => {
            document.head.removeChild(styleEl)
        }
    }, [])
    return (
        <Context.Provider
            value={{
                menu,
                parent,
                internalMenu,
                activeInternalMenu,
                fullWidthContent,
                setFullWidthContent,
                logo,
                setLogo,
                brand,
                setBrand,
                sidebars,
                setSidebars,
            }}
        >
            {children}
        </Context.Provider>
    )
}

export const useLayoutData = () => {
    const layoutData = useContext(Context)
    return layoutData
}
